import * as math from 'mathjs';
import SCARARTrafo from "./SCARARTrafo";

class IkinCartSCARA {
  /**
   * Inverse kinematics for SCARA robot using analytical method
   * Based on SCARA_RTRAFO
   *
   * @param {Matrix} F - Series of target frames (3x4xN or 4x4xN)
   * @param {Array|Matrix} q - Initial joint angles (last solution or start pose)
   * @param {Object} r - Robot object with joint properties
   * @param {Array} mask - Mask vector (not used in this implementation)
   * @param {number} tIpo - Time step (not used in this implementation)
   * @returns {Array} [qs, status] - qs: array of solution joint angles for each pose, status: 'ok' or 'Probably out of workspace'
   */
  run(F, q, r, mask, tIpo) {
    let scaraRTrafo = new SCARARTrafo();

    // Extract number of poses from F (3rd dimension)
    let F_size = F.size();
    let zahlposen = F_size.length >= 3 ? F_size[2] : 1;

    let q_alt = [...math.squeeze(q).toArray ? math.squeeze(q).toArray() : math.squeeze(q)];
    let qs = []; // Solutions for each pose
    let status = 'ok'; // Vorbesetzung

    let N = r.jointAmount; // Number of joints (4 for SCARA)
    let hh = r.joints.map(j => j.h); // Joint types (1 = rotational, 0 = prismatic)

    let qmax = r.joints.map(j => j.qmax);
    let qmin = r.joints.map(j => j.qmin);

    let eps_q = 0.0001; // Used for mapping rotational joints to working range

    // Gewichtungen für Gelenk 1 bis Gelenk 4, Summe ist 1
    let c_g = [0.45, 0.35, 0.1, 0.1];
    let d_g = math.diag(c_g);

    // Loop over all intermediate poses
    for (let i = 0; i < zahlposen; i++) {
      let anz = 0; // Number of valid solutions for this pose
      let qloes = []; // All valid solutions for this pose

      // Extract current pose F_akt = F(:,:,i) -> index i (0-based)
      let F_akt = math.subset(F, math.index([0, 1, 2], math.range(0, 4), i));
      F_akt = math.squeeze(F_akt);

      // Call SCARA_RTRAFO to get both possible solutions
      let qz = scaraRTrafo.run(F_akt, q_alt, r);

      // Check if qz is empty (warnung == 'ausserhalb')
      if (!qz || (Array.isArray(qz) && qz.length === 0)) {
        status = 'Probably out of workspace';
        return [qs, status];
      }

      // qz is a matrix with 2 solution vectors (4x2)
      for (let iz = 0; iz < 2; iz++) {
        // Extract solution vector q = qz(:,iz+1) in MATLAB = qz[:,iz] in JS (0-based)
        let q_col = math.subset(qz, math.index(math.range(0, N), iz));
        q_col = math.squeeze(q_col);
        let q = [...(q_col.toArray ? q_col.toArray() : q_col)];

        // out_of_range script (matches MATLAB out_of_range.m)
        // Maps rotational joints to -2π to 2π range, then tries to shift into qmin/qmax range
        for (let ig = 0; ig < N; ig++) {
          if (hh[ig] === 1) { // rotat. Gelenk
            q[ig] = q[ig] - Math.floor(q[ig] / (2 * Math.PI + eps_q)) * 2 * Math.PI;

            if (qmin[ig] >= 0 && qmax[ig] > 0 && q[ig] < 0) {
              q[ig] = q[ig] + 2 * Math.PI;
            }

            if (qmin[ig] < 0 && qmax[ig] >= 0) {
              if (q[ig] > qmax[ig]) {
                q[ig] = q[ig] - 2 * Math.PI;
              }
              if (q[ig] < qmin[ig]) {
                q[ig] = q[ig] + 2 * Math.PI;
              }
            }

            if (qmin[ig] < 0 && qmax[ig] <= 0 && q[ig] > 0) {
              q[ig] = q[ig] - 2 * Math.PI;
            }
          }
        }

        // Check if all joints are within qmin and qmax
        let out_range = 0;
        for (let k = 0; k < N; k++) {
          if (q[k] > (qmax[k] + eps_q) || q[k] < (qmin[k] - eps_q)) {
            out_range = 1;
          }
        }

        if (out_range === 0) {
          anz = anz + 1;
          qloes.push(q);
        }
      }

      if (anz > 0) {
        // Calculate weighted distances to q_alt for all valid solutions
        let abw = [];
        for (let i1 = 0; i1 < anz; i1++) {
          let dq = q_alt.map((q_alt_val, idx) => q_alt_val - qloes[i1][idx]);
          let dq_gew = math.multiply(d_g, math.matrix(dq));
          abw.push(math.norm(dq_gew));
        }

        // Find solution with minimum weighted distance
        let min_abw = Math.min(...abw);
        let min_loes = abw.indexOf(min_abw);

        let q = qloes[min_loes];
        qs.push(q);
        q_alt = q;
        abw = [];
      } else {
        status = 'Probably out of workspace';
        return [qs, status];
      }
    }

    return [qs, status];
  }
}

export default IkinCartSCARA;
